import type { QuestionnaireResultResponse } from '@gcv/shared';
import { BankTemplateCategory, QuestionnaireStatus, SuppressedReason } from '@gcv/shared';

import { ZeroState } from 'ui';
import { getLatestRevisionNote } from './get-latest-revision-note';
import { hasPendingRevision } from './has-pending-revision';

const zeroState = (title: string, subTitle: string) => (
  <ZeroState
    title={title}
    subTitle={subTitle}
    icon="/img/empty-states/no-questionnaires-in-progress.svg"
    noBorder
  />
);

export const getQuestionnaireZeroState = (questionnaireResult: QuestionnaireResultResponse | undefined) => {
  if (!questionnaireResult) {
    return zeroState('Could not find questionnaire', 'Contact support if issue persists.');
  }

  const {
    custom_requirement_completed_note,
    is_org_archived,
    revision_history,
    status,
    suppressed_reason,
    template_category
  } = questionnaireResult;

  if (hasPendingRevision(revision_history)) {
    const note = getLatestRevisionNote(revision_history);
    return zeroState(
      'Revision requested — waiting for the business to resubmit',
      note
        ? `The business has been notified of the revision request with the following note: "${note}". You will see updated responses once they resubmit.`
        : 'The business has been notified of the revision request. You will see updated responses once they resubmit.'
    );
  }

  if (is_org_archived === true) {
    return zeroState(
      'This account was archived and the questionnaire was autocompleted.',
      `Completed note: ${custom_requirement_completed_note}`
    );
  }

  if (status === QuestionnaireStatus.Open) {
    return zeroState(
      'Questionnaire was sent but hasn’t been started yet',
      'Once started, the status will change above. You will see the responses to this questionnaire once it’s been submitted.'
    );
  }

  if (
    status === QuestionnaireStatus.Suppressed &&
    template_category === BankTemplateCategory.UnverifiedSales
  ) {
    if (suppressed_reason === SuppressedReason.BELOW_THRESHOLD) {
      return zeroState(
        'Unverified Sales Below Threshold.',
        'This questionnaire was suppressed because the percentage of unverified sales did not exceed your defined threshold. No action is required unless discrepancies increase in future periods.'
      );
    }
    return zeroState(
      'No Unverified Sales for This Period.',
      'This questionnaire was suppressed because the CRB did not have any unverified sales for the selected timeframe. No action is required.'
    );
  }

  if (status === QuestionnaireStatus.Suppressed && template_category === BankTemplateCategory.SalesUpload) {
    if (suppressed_reason === SuppressedReason.HAS_MANUAL_UPLOAD_SALES) {
      return zeroState(
        'Sales Data Already Uploaded.',
        'This questionnaire was suppressed because the CRB has already uploaded their sales data for this period. No further action is required.'
      );
    }
    if (suppressed_reason === SuppressedReason.POS_CONNECTED) {
      return zeroState(
        'POS Integration Connected.',
        'This questionnaire was suppressed because the CRB’s point-of-sale system is now connected to Green Check. Manual uploads are not required.'
      );
    }
    return zeroState(
      'This questionnaire was suppressed.',
      'A questionnaire is suppressed when the account has uploaded sales or integrated with a POS.'
    );
  }

  // Completed results only fall through to the zero state when the questionnaire itself failed to load.
  if (status === QuestionnaireStatus.Completed) {
    return zeroState('Could not load questionnaire', 'Contact support if issue persists.');
  }

  return zeroState(
    'This questionnaire is in progress and hasn’t been submitted yet',
    'Once submitted, you will see the questions and responses submitted by the account on this page.'
  );
};
